import { HttpError } from 'react-admin';
import Auth from '@aws-amplify/auth';

import { getLocalStorage } from 'services/helpers/local-storage';
import { signOut } from 'services/helpers/signOut';

const defaultErrorMessages = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Not authorized. Please log in again.',
  403: 'Access denied. You do not have permission to perform this action. If you believe this is an error, please contact support.',
  404: 'Resource not found. The requested item may not exist. Please try again.',
  408: 'Connection timeout. Please check your network and try again.',
  409: 'Conflict detected. Please review your request and try again.',
  410: 'Resource is no longer available. Please refresh or contact support.',
  429: 'Too many requests. Please wait and try again later.',
  500: 'Internal server error. Something went wrong. Please try again or contact support.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service unavailable. Please try again in a few minutes.',
  504: 'Server timeout. Please try again later.',
};

interface FetchOptions {
  headers?: Headers;
  body?: BodyInit;
  user?: {
    authenticated: boolean;
    token: string;
  };
  [key: string]: any;
}

const refreshToken = async () => {
  try {
    const currentSession = await Auth.currentSession();
    const cognitoUser = await Auth.currentAuthenticatedUser();

    await new Promise((resolve, reject) => {
      cognitoUser.refreshSession(currentSession.getRefreshToken(), (err, session) => {
        if (err) {
          reject(err);
        } else {
          resolve(session);
        }
      });
    });

    // Get the new session after refresh
    const newSession = await Auth.currentSession();
    const newIdToken = newSession.getIdToken();

    console.log('Token refreshed successfully');

    // Dispatch custom event to notify that token was refreshed
    window.dispatchEvent(new CustomEvent('tokenRefreshed'));

    return newIdToken.getJwtToken();
  } catch (error: any) {
    console.error('Error refreshing token:', error);
    // Handle refresh token failure - redirect to login
    await signOut();
    throw error;
  }
};

const fetchJson = async (url: string, options: FetchOptions = {}): Promise<any> => {
  const makeRequest = async (
    retryCount = 0,
    requestOptions: FetchOptions = options
  ): Promise<any> => {
    const requestHeaders = new Headers(
      requestOptions.headers || {
        Accept: 'application/json',
      }
    );

    if (
      !requestHeaders.has('Content-Type') &&
      !(requestOptions.body && requestOptions.body instanceof FormData)
    ) {
      requestHeaders.set('Content-Type', 'application/json');
    }

    if (requestOptions.user && requestOptions.user.authenticated && requestOptions.user.token) {
      requestHeaders.set('Authorization', requestOptions.user.token);
    }

    const response = await fetch(url, { ...requestOptions, headers: requestHeaders });

    const text = await response.text();

    const mappedResponse = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      body: text,
    };

    let json;

    try {
      json = JSON.parse(mappedResponse.body);
    } catch (e) {
      // not json, no big deal
    }

    if (mappedResponse.status === 307) {
      // Prevent infinite loop by limiting retries
      if (retryCount >= 2) {
        throw new HttpError(
          'Token refresh failed after multiple attempts',
          mappedResponse.status,
          json || mappedResponse.body
        );
      }

      // Token needs refresh, attempt to refresh and retry
      const newToken = await refreshToken();

      // Update both the Authorization header and the user token
      const bearerToken = `Bearer ${newToken}`;

      // Create new options with updated token
      const newOptions = {
        ...requestOptions,
        user: {
          ...requestOptions.user,
          authenticated: true,
          token: bearerToken,
        },
      };

      // Create new headers with updated token
      const newHeaders = new Headers(requestHeaders);
      newHeaders.set('Authorization', bearerToken);
      newOptions.headers = newHeaders;

      return makeRequest(retryCount + 1, newOptions); // Pass the new options to the retry
    }

    if (mappedResponse.status < 200 || mappedResponse.status >= 300) {
      let defaultErrorMessage = '';

      if ((json && !json?.message) || !mappedResponse?.body) {
        const userSub = getLocalStorage('user-sub');

        defaultErrorMessage = `${mappedResponse.status} - ${
          defaultErrorMessages[mappedResponse.status]
        } UserId: ${userSub}  Timestamp: ${json?.timestamp ?? new Date(Date.now()).toISOString()}`;
      }

      throw new HttpError(
        (json && json.error && json.error.message) ||
          (json && json.message) ||
          defaultErrorMessage ||
          mappedResponse.body ||
          mappedResponse.statusText,
        mappedResponse.status,
        json || mappedResponse.body
      );
    }

    return Promise.resolve({
      status: mappedResponse.status,
      headers: mappedResponse.headers,
      body: mappedResponse.body,
      json: json,
    });
  };

  return makeRequest();
};

export default fetchJson;
